import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import PageHero from "../components/PageHero";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  UsersRound, Plus, Crown, ShieldCheck, ArrowRight, Circle,
} from "lucide-react";

const TEAL = "#0D9488";

export default function Groups() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ is_coach: false, can_create: false, is_admin: false, active_group_id: null, active_group_name: null });
  const [openCreate, setOpenCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [listRes, statusRes] = await Promise.all([
        api.get("/groups"),
        api.get("/groups/my-status").catch(() => ({ data: { is_coach: false, can_create: false } })),
      ]);
      setItems(listRes.data.items || []);
      setStatus(statusRes.data || {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const canCreate = !!status.can_create;
  const showCreateButton = canCreate || status.is_admin;

  return (
    <AppLayout>
      <PageHero
        eyebrow="Coaching"
        title="Groups"
        subtitle="Private group coaching spaces. Coaches run their crew, members share progress, everyone shows up together."
        icon={<UsersRound className="w-8 h-8 text-[#0D9488]" />}
        gradient
        actions={
          showCreateButton ? (
            <Button
              onClick={() => setOpenCreate(true)}
              className="text-white"
              style={{ background: TEAL }}
              data-testid="group-create-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" /> New Group
            </Button>
          ) : null
        }
      />

      {status.is_coach && !status.is_admin && !canCreate && status.active_group_id && (
        <div
          className="mb-6 border border-amber-300 bg-amber-50 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3"
          data-testid="group-limit-banner"
        >
          <Crown className="w-5 h-5 text-amber-600 shrink-0" />
          <div className="flex-1 text-sm text-amber-900">
            <b>You already have an active group.</b> Manage your existing group or close it before creating a new one.
          </div>
          <Link
            to={`/groups/${status.active_group_id}`}
            data-testid="group-limit-open-active"
            className="shrink-0"
          >
            <Button variant="outline" className="border-amber-400 text-amber-800 hover:bg-amber-100">
              Manage "{status.active_group_name || "your group"}"
            </Button>
          </Link>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="groups-loading">
          {[0, 1, 2].map((i) => (
            <Card key={i} className="border border-slate-200 rounded-xl p-6 h-40 animate-pulse bg-white" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <Card className="border border-dashed border-slate-200 rounded-xl p-12 text-center bg-white" data-testid="groups-empty">
          <UsersRound className="w-8 h-8 mx-auto text-slate-300 mb-3" />
          <div className="font-display font-bold text-lg text-slate-900 mb-1">
            {showCreateButton ? "You haven't created a group yet" : "You aren't in a group yet"}
          </div>
          <div className="text-sm text-slate-500 max-w-md mx-auto">
            {showCreateButton
              ? "Create your first group — invite a few clients, post announcements, and run weekly check-ins in one place."
              : "Your coach will invite you here. Once you're in, you'll see your group's feed, weekly check-ins, and pinned resources."}
          </div>
          {showCreateButton && (
            <Button
              onClick={() => setOpenCreate(true)}
              className="mt-5 text-white"
              style={{ background: TEAL }}
              data-testid="groups-empty-create"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Create your first group
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="groups-grid">
          {items.map((g) => (
            <GroupCard key={g.id} g={g} />
          ))}
        </div>
      )}

      <CreateGroupDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onCreated={(gid) => { setOpenCreate(false); toast.success("Group created"); load(); }}
      />
    </AppLayout>
  );
}

function GroupCard({ g }) {
  const isCoach = g.viewer_role === "coach";
  const dissolved = g.is_dissolved;
  const closed = g.is_closed;
  return (
    <Link to={`/groups/${g.id}`} data-testid={`group-card-${g.id}`}>
      <Card
        className={`p-5 rounded-xl border transition-shadow hover:shadow-md bg-white dark:bg-slate-900 ${
          dissolved || closed ? "border-slate-200 opacity-70" : "border-slate-200 dark:border-slate-700"
        }`}
      >
        <div className="flex items-start gap-3 mb-3">
          <div
            className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0 text-white font-bold"
            style={{ background: "linear-gradient(135deg,#5EEAD4 0%,#0D9488 100%)" }}
          >
            <UsersRound className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-base text-slate-900 dark:text-slate-100 truncate">
              {g.name}
            </div>
            <div className="text-xs text-slate-500 truncate mt-0.5">
              {g.coach?.name ? `Coached by ${g.coach.name}` : "—"}
            </div>
          </div>
          {dissolved ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold bg-slate-200 text-slate-600">
              Dissolved
            </span>
          ) : closed ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold bg-amber-100 text-amber-700" data-testid={`group-card-closed-${g.id}`}>
              Closed
            </span>
          ) : isCoach ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold bg-amber-100 text-amber-700">
              <Crown className="w-2.5 h-2.5" /> Coach
            </span>
          ) : g.viewer_role === "admin" ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold bg-red-100 text-red-700">
              <ShieldCheck className="w-2.5 h-2.5" /> Admin
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold bg-teal-50 text-teal-700">
              <Circle className="w-2 h-2 fill-current" /> Member
            </span>
          )}
        </div>
        {g.description && (
          <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2 mb-3">{g.description}</p>
        )}
        <div className="flex items-center justify-between text-xs text-slate-500 font-mono-num">
          <span>{g.member_count} member{g.member_count === 1 ? "" : "s"}</span>
          <span className="inline-flex items-center gap-1 text-teal-700 font-semibold uppercase tracking-widest text-[10px]">
            Open <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </Card>
    </Link>
  );
}

function CreateGroupDialog({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/groups", {
        name: name.trim(),
        description: description.trim(),
      });
      onCreated?.(data.id);
      setName(""); setDescription("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-md" data-testid="group-create-dialog">
        <DialogHeader>
          <DialogTitle>New group</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="g-name">Group name</Label>
            <Input
              id="g-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Q1 Recomp Crew"
              maxLength={80}
              required
              data-testid="group-create-name"
            />
          </div>
          <div>
            <Label htmlFor="g-desc">Description</Label>
            <Textarea
              id="g-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this group about? What can members expect?"
              rows={3}
              maxLength={600}
              data-testid="group-create-desc"
            />
          </div>
          <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
            You can have <b>one active group at a time</b>. Invite as many members as you want. Close the group when you're done to start a new one.
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button
              type="submit"
              disabled={busy || !name.trim()}
              className="text-white"
              style={{ background: TEAL }}
              data-testid="group-create-submit"
            >
              {busy ? "Creating…" : "Create group"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
